import React from 'react';
import clsx from 'clsx';
import { DialogClose, DialogTitle } from '@radix-ui/react-dialog';
import { Stack, Tag, Box, Text, StackItem, Heading, Dialog, Button, ToneFocus } from '..';
import { ArrowDropdownRightIcon, ListIcon } from '../../icons';
import { useMedia } from '../../hooks';
import { mq } from '../../global/breakpoints';

import * as styles from './TableOfContent.css';

type TableOfContentProps = {
  title: string;
  items: {
    title: string;
    anchorId: string;
    isActive: boolean;
  }[];
  className?: string;
};

export const TableOfContent = ({ title, items, className }: TableOfContentProps) => {
  const isDesktop = useMedia(mq.md);

  return isDesktop ? (
    <Box
      className={className}
      width="100%"
      padding={6}
      borderRadius="brandXl"
      bg="containerLight"
      asChild
    >
      <Stack direction="column" gap={6}>
        {title ? (
          <StackItem>
            <Tag>{title}</Tag>
          </StackItem>
        ) : null}

        <Stack direction="column" gap={4} as="ul">
          {items.map((item) => (
            <Box key={item.anchorId} as="li">
              <Stack direction="row" gap={1} className={styles.listItem} asChild>
                <ToneFocus tone="onLight">
                  <Text size="label" weight="semiBold" asChild>
                    <a
                      href={`#${item.anchorId}`}
                      className={clsx({
                        [styles.link]: !item.isActive,
                        [styles.linkActive]: item.isActive,
                      })}
                    >
                      {item.isActive ? (
                        <Box>
                          <ArrowDropdownRightIcon className={styles.listItemActiveIcon} size="xs" />
                        </Box>
                      ) : null}
                      {item.title}
                    </a>
                  </Text>
                </ToneFocus>
              </Stack>
            </Box>
          ))}
        </Stack>
      </Stack>
    </Box>
  ) : (
    <Dialog
      trigger={
        <Button
          className={styles.mobileToggleButton}
          variant="primary"
          afterIcon={<ListIcon size="sm" />}
        >
          {title}
        </Button>
      }
    >
      <Stack direction="column" isFullWidth isFullHeight alignY="justify" gap={4}>
        {title ? (
          <Box padding={6}>
            <DialogTitle asChild>
              <Heading size="h2">{title}</Heading>
            </DialogTitle>
          </Box>
        ) : null}
        <Stack direction="column" isFullWidth>
          <Box padding={6} as="ul">
            {items.map((item) => (
              <Box key={item.anchorId} className={styles.listItem} as="li">
                <ToneFocus tone="onLight">
                  <Text size="label" weight={item.isActive ? 'semiBold' : 'regular'} asChild>
                    <DialogClose asChild>
                      <a
                        href={`#${item.anchorId}`}
                        className={clsx({
                          [styles.link]: !item.isActive,
                          [styles.linkActive]: item.isActive,
                        })}
                      >
                        {item.isActive ? <ArrowDropdownRightIcon size="xs" /> : null}
                        {item.title}
                      </a>
                    </DialogClose>
                  </Text>
                </ToneFocus>
              </Box>
            ))}
          </Box>
          <DialogClose asChild>
            <Button
              className={styles.mobileToggleButton}
              variant="primary"
              afterIcon={<ListIcon size="sm" />}
            >
              {title}
            </Button>
          </DialogClose>
        </Stack>
      </Stack>
    </Dialog>
  );
};

